import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { usePostLoginRedirect } from "@/hooks/usePostLoginRedirect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Lock, User, Building2, Globe2, Phone, ChevronLeft, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { AuthShell } from "@/components/Auth/AuthShell";

const emailSchema = z.string().email("Invalid email address");
const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, "Password must contain at least one number or special character");

const ORG_TYPES = [
  { value: 'parliament',   label: 'Parliament / Legislature' },
  { value: 'ministry',     label: 'Ministry / Government Agency' },
  { value: 'news_agency',  label: 'News Agency / Media House' },
  { value: 'broadcaster',  label: 'Broadcaster (TV / Radio)' },
  { value: 'ngo',          label: 'NGO / Civil Society' },
  { value: 'regulator',    label: 'Media Regulator' },
  { value: 'other',        label: 'Other' },
];

const Auth = () => {
  const navigate = useNavigate();
  const { redirect } = usePostLoginRedirect();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  const defaultTab = searchParams.get("mode") === "signup" ? "signup" : "signin";

  const [signInData, setSignInData] = useState({ email: "", password: "" });
  const [signUpData, setSignUpData] = useState({
    email: "", password: "", fullName: "",
    orgName: "", orgType: "", country: "", phone: "",
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) redirect();
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) redirect();
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      emailSchema.parse(signInData.email);
      passwordSchema.parse(signInData.password);
      const { error } = await supabase.auth.signInWithPassword({
        email: signInData.email,
        password: signInData.password,
      });
      if (error) throw error;
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({ title: "Validation error", description: err.errors[0].message, variant: "destructive" });
      } else {
        toast({ title: "Sign in failed", description: (err as Error).message, variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      emailSchema.parse(resetEmail);
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth?mode=signin`,
      });
      if (error) throw error;
      toast({ title: "Reset link sent", description: "Check your inbox." });
      setForgotMode(false);
      setResetEmail("");
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({ title: "Invalid email", description: err.errors[0].message, variant: "destructive" });
      } else {
        toast({ title: "Error", description: (err as Error).message, variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      emailSchema.parse(signUpData.email);
      passwordSchema.parse(signUpData.password);
      if (!signUpData.fullName.trim()) throw new Error("Full name is required");
      if (!signUpData.orgName.trim()) throw new Error("Organisation name is required");

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: signUpData.email,
        password: signUpData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/setup`,
          data: { full_name: signUpData.fullName },
        },
      });
      if (signUpError) {
        if (signUpError.message.includes("already registered")) {
          toast({ title: "Account already exists", description: "Please sign in instead.", variant: "destructive" });
          return;
        }
        throw signUpError;
      }
      if (!authData.user) throw new Error("Account creation failed");

      await (supabase
        .from('institution_requests' as any)
        .insert({
          user_id:   authData.user.id,
          email:     signUpData.email,
          full_name: signUpData.fullName,
          org_name:  signUpData.orgName,
          org_type:  signUpData.orgType || null,
          country:   signUpData.country || null,
          phone:     signUpData.phone || null,
          status:    'pending',
        }) as any);

      setRequestSent(true);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({ title: "Validation error", description: err.errors[0].message, variant: "destructive" });
      } else {
        toast({ title: "Request failed", description: (err as Error).message, variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      logoLabel="Institutions"
      title={requestSent ? "Request received" : forgotMode ? "Reset your password" : t('auth.title')}
      subtitle={requestSent ? undefined : forgotMode ? "We'll send you a reset link." : "Sign in to your account. You'll be redirected based on your role."}
      quote="Communications, in orbit."
      quoteAuthor="For institutional press operations"
    >
      {requestSent ? (
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-success" />
          </div>
          <p className="text-justify text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
            Your institution request has been submitted. Our team will review it and contact you at{" "}
            <strong className="text-foreground">{signUpData.email}</strong> within 24–48 hours.
          </p>
          <p className="text-justify text-xs text-muted-foreground">Please check your email for a verification link first.</p>
          <Link to="/"><Button variant="outline" className="w-full">Back to homepage</Button></Link>
        </div>
      ) : forgotMode ? (
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <button
            type="button"
            onClick={() => setForgotMode(false)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-4 h-4" /> Back to sign in
          </button>
          <div className="space-y-2">
            <Label htmlFor="reset-email">{t('auth.email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input id="reset-email" type="email" placeholder="you@example.com"
                value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} className="pl-10 h-11" required />
            </div>
          </div>
          <Button type="submit" className="w-full h-11" disabled={loading}>
            {loading ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      ) : (
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 h-11">
            <TabsTrigger value="signin">Sign In</TabsTrigger>
            <TabsTrigger value="signup">Request Access</TabsTrigger>
          </TabsList>

          <TabsContent value="signin">
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signin-email">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="signin-email" type="email" placeholder="you@example.com"
                    value={signInData.email}
                    onChange={(e) => setSignInData(d => ({ ...d, email: e.target.value }))}
                    className="pl-10 h-11" autoComplete="email" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="signin-password">{t('auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="signin-password" type="password" placeholder="••••••••"
                    value={signInData.password}
                    onChange={(e) => setSignInData(d => ({ ...d, password: e.target.value }))}
                    className="pl-10 h-11" autoComplete="current-password" required />
                </div>
              </div>
              <Button type="submit" className="w-full h-11 shadow-glow" disabled={loading}>
                {loading ? t('auth.signing_in') : t('auth.sign_in')}
              </Button>
              <button type="button" onClick={() => setForgotMode(true)}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground">
                Forgot password?
              </button>
            </form>
          </TabsContent>

          <TabsContent value="signup">
            <form onSubmit={handleSignUp} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="signup-name">Your Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="signup-name" placeholder="Jane Appiah" value={signUpData.fullName}
                    onChange={(e) => setSignUpData(d => ({ ...d, fullName: e.target.value }))}
                    className="pl-10 h-11" autoComplete="name" required />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-email">Work Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="signup-email" type="email" placeholder="you@org.org" value={signUpData.email}
                    onChange={(e) => setSignUpData(d => ({ ...d, email: e.target.value }))}
                    className="pl-10 h-11" autoComplete="email" required />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-password">Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="signup-password" type="password" placeholder="Min. 8 characters" value={signUpData.password}
                    onChange={(e) => setSignUpData(d => ({ ...d, password: e.target.value }))}
                    className="pl-10 h-11" autoComplete="new-password" required />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-org">Organisation Name *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="signup-org" placeholder="ECOWAS Parliament" value={signUpData.orgName}
                    onChange={(e) => setSignUpData(d => ({ ...d, orgName: e.target.value }))}
                    className="pl-10 h-11" required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="signup-type">Org. Type</Label>
                  <select id="signup-type" value={signUpData.orgType}
                    onChange={(e) => setSignUpData(d => ({ ...d, orgType: e.target.value }))}
                    className="w-full h-11 border border-input rounded-md px-3 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring">
                    <option value="">Select…</option>
                    {ORG_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-country">Country</Label>
                  <div className="relative">
                    <Globe2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="signup-country" placeholder="Nigeria" value={signUpData.country}
                      onChange={(e) => setSignUpData(d => ({ ...d, country: e.target.value }))} className="pl-10 h-11" />
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-phone">Phone (optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="signup-phone" type="tel" placeholder="+234 800 000 0000" value={signUpData.phone}
                    onChange={(e) => setSignUpData(d => ({ ...d, phone: e.target.value }))} className="pl-10 h-11" />
                </div>
              </div>
              <p className="text-justify text-[11px] text-muted-foreground pt-1 leading-relaxed">
                By submitting, you agree to CONORBIT's{" "}
                <Link to="/terms" className="underline hover:text-foreground">Terms</Link> and{" "}
                <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
                Our team will review and contact you within 24–48 hours.
              </p>
              <Button type="submit" className="w-full h-11 shadow-glow" disabled={loading}>
                {loading ? "Submitting request…" : "Request Institution Access"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      )}
    </AuthShell>
  );
};

export default Auth;
